import React, { useRef, useState } from "react";
import { Grid, TextField, Typography } from "@mui/material";
import { debounce } from "lodash/fp";
import { useMutation } from "@apollo/client/react";
import OverwatchModal from "@/components/OverwatchModal";
import { CompanyResource, CreateUserDocument } from "@/graphql/generated/graphql";
import { displayNotification } from "@/utils/notifications";
import EssealPhoneInput from "@/components/EssealPhoneInput";

const normalDebounce = debounce(500);

interface CreateResourceUserProps {
  resource: CompanyResource;
  handleClose: () => void;
}

const DEFAULT_USER_DATA = {
  firstName: "",
  lastName: "",
  email: "",
  primaryPhone: "",
  bio: "",
}

const CreateResourceUser: React.FC<CreateResourceUserProps> = ({
  resource,
  handleClose,
}) => {

  const debounceFunc = useRef(normalDebounce((fn) => fn())).current;
  const [user, setUser] = useState({
    ...DEFAULT_USER_DATA,
    firstName: resource.user?.firstName || resource.name || "",
    lastName: resource.user?.lastName || "",
    bio: resource.user?.bio || resource.resourceMeta?.bio || "",
    email: resource.user?.email || resource.resourceMeta?.email || "",
    primaryPhone: resource.user?.primaryPhone || resource.resourceMeta?.primaryPhone || ""
  });

  const [createUser] = useMutation(CreateUserDocument, {
    refetchQueries: ["getCompanyResources", "getAllEmployees"],
  });

  const disableCreate = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return !!resource?.user || !(user.email && emailRegex.test(user.email) && user.firstName && user.lastName);
  }

  const handleOnChange = (key: string, value: string) => {
    debounceFunc.cancel();
    debounceFunc(() => {
      setUser((prev) => ({
        ...prev,
        [key]: value,
      }));
    });
  };

  const handleSubmit = () => {
    createUser({
      variables: { ...user, companyResourceId: resource.id },
      onCompleted: () => {
        handleClose();
        displayNotification({
          type: "success",
          text: "User Invited Successfully",
        });
      },
      onError: () => {
        displayNotification({
          type: "error",
          text: "Unable to invite user. Please try again later.",
        });
      }
    });
  };

  return (
    <OverwatchModal
      size="sm"
      title="Setup Overwatch Account"
      handleClose={handleClose}
      primaryButtonText="Send Invite"
      primaryButtonAction={handleSubmit}
      disablePrimary={disableCreate()}
    >
      <Grid container spacing={2}>
        {resource?.user &&
          <Grid size={12}>
            <Typography variant="body2" color="error" padding={2}>
              An invitation has already been sent for this Resource.
            </Typography>
          </Grid>
        }
        <Grid
          size={{
            xs: 12,
            sm: 6
          }}>
          <TextField
            autoComplete="given-name"
            name="firstName"
            defaultValue={user.firstName}
            required
            fullWidth
            id="firstName"
            label="First Name"
            autoFocus
            onChange={(e) => handleOnChange("firstName", e.target.value)}
            disabled={!!resource?.user}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6
          }}>
          <TextField
            required
            fullWidth
            id="lastName"
            label="Last Name"
            name="lastName"
            autoComplete="family-name"
            defaultValue={user.lastName}
            onChange={(e) => handleOnChange("lastName", e.target.value)}
            disabled={!!resource?.user}
          />
        </Grid>
        <Grid size={12}>
          <TextField
            required
            fullWidth
            id="email"
            label="Email Address"
            name="email"
            defaultValue={user.email}
            autoComplete="email"
            onChange={(e) => handleOnChange("email", e.target.value)}
            disabled={!!resource?.user}
          />
        </Grid>
        <Grid size={12}>
          <EssealPhoneInput
            style={{ width: "100%"}}
            id="phone"
            label="Phone"
            name="phone"
            value={user.primaryPhone}
            onChange={(e164) => handleOnChange("primaryPhone", e164)}
            defaultCountry="GB"
            disabled={!!resource?.user}
          />
        </Grid>
        <Grid size={12}>
          <TextField
            fullWidth
            multiline
            rows={5}
            id="bio"
            label="Bio"
            name="bio"
            autoComplete="bio"
            defaultValue={user.bio}
            onChange={(e) => handleOnChange("bio", e.target.value)}
            disabled={!!resource?.user}
          />
        </Grid>
      </Grid>
    </OverwatchModal>
  );
};

export default CreateResourceUser;
